import * as THREE from "../../libs/three.js/build/three.module.js";
import {DigitizationTools} from "./ToolRegistry.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {createFeature} from "./Feature.js";

import {T} from "./i18n/index.js";
// Den native 2D-profil (Potrees egen ProfileTool/Profile) som en Registrering under Målinger.
//
// Profilen indtegnes stadig af Potrees egen ProfileTool og vises i det native profilvindue; det her gør den til et
// persistent, genåbneligt objekt i Registreringer/Målinger-træet ved at spejle den som en almindelig feature
// (LineString) - samme livscyklus som alle andre registreringer (valg, omdøb, zoom, slet, model-tilknytning).
//
//   profil-geometri  <->  feature-geometri   (redigering sker på featurens vertex-håndtag; spejlet skrives til profilen)
//   profil.visible   <->  feature-visuals øje
//   slet feature     ->   profilen fjernes fra scenen
//   profil fjernet   ->   featuren fjernes (fx modelskift)
//
// Bredden er profilens EGEN (profile.width) og dubleres ikke her.

export const NATIVE_PROFILE_TOOL_ID = "digitize_profile_native";

// Findes kun for at klassificere profilen (kind = measurement) og give den navn/ikon i træet. Den startes ikke fra
// værktøjsmenuerne (ingen toolbarGroup) - den native profil har sin egen adgang i Analysevisninger.
export const NativeProfileTool = {
	id: NATIVE_PROFILE_TOOL_ID,
	name: T("2D-profil"),
	category: "hidden",
	kind: "measurement",
	icon: "profile.svg",
	resultGeometryType: "LineString",
	featureType: T("2D-profil"),
	tooltip: T("Native 2D-profil (længdesnit i punktskyen)."),
	customCapture(){
		// Intet: den native profil indtegnes af Potrees ProfileTool.
	},
};

DigitizationTools.register(NativeProfileTool);

function toVector3(coord){
	return new THREE.Vector3(coord[0], coord[1], coord[2]);
}

// Skriver featurens (redigerede) geometri tilbage til profilen. Samme antal punkter: flyt dem; ellers genopbyg.
function syncProfileFromVisual(profile, visual){
	const coords = visual.toCoordinates();
	if(coords.length < 2) return;

	if(coords.length === profile.points.length){
		coords.forEach((c, i) => profile.setPosition(i, toVector3(c)));
		return;
	}

	while(profile.points.length > 0){
		profile.removeMarker(profile.points.length - 1);
	}
	coords.forEach((c) => profile.addMarker(toVector3(c)));
	profile.modifiable = false;
}

// Spejler en færdig profil (mindst to punkter) som en feature. Idempotent pr. profil. Returnerer feature-id'et.
export function registerNativeProfile(dm, profile){
	if(!profile || !profile.points || profile.points.length < 2) return null;

	for(const [featureId, existing] of dm.visuals){
		if(existing.userData.nativeProfile && existing.userData.nativeProfile.profile === profile) return featureId;
	}

	const scene = dm.viewer.scene;
	const visual = new FeatureVisual("LineString");
	dm.scene.add(visual);
	visual.setPoints(profile.points.map((p) => p.clone()));

	const number = [...dm.visuals.values()].filter((v) => v.userData.nativeProfile).length + 1;
	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: NativeProfileTool.category,
		tool: NATIVE_PROFILE_TOOL_ID,
		featureType: NativeProfileTool.featureType,
		name: `${NativeProfileTool.name} ${number}`,
		attributes: {nativeProfileUuid: profile.uuid},
	});
	visual.setInfo(feature.properties.name, feature.properties.description);
	visual.userData.nativeProfile = {profile};

	// Træets øje (visual.visible) styrer også profilens egen visning (korridor m.m.).
	let shown = true;
	Object.defineProperty(visual, "visible", {
		configurable: true,
		get: () => shown,
		set: (value) => { shown = !!value; profile.visible = shown; },
	});

	// Featuren er nu den ene redigerbare overflade - profilens egne håndtag skjules.
	profile.modifiable = false;

	dm._wireFeatureLifecycle(feature, visual);

	visual.addEventListener("geometry_changed", () => syncProfileFromVisual(profile, visual));

	let released = false;
	const release = () => {
		if(released) return false;
		released = true;
		scene.removeEventListener("profile_removed", onProfileRemoved);
		dm.featureManager.removeEventListener("feature_removed", onFeatureRemoved);
		return true;
	};
	const onProfileRemoved = (e) => {
		if(e.profile !== profile) return;
		if(!release()) return;
		if(dm.getVisual(feature.id)) dm.removeFeature(feature.id);
	};
	const onFeatureRemoved = (e) => {
		if(!e.feature || e.feature.id !== feature.id) return;
		if(!release()) return;
		if(scene.profiles.indexOf(profile) !== -1) scene.removeProfile(profile);
	};
	scene.addEventListener("profile_removed", onProfileRemoved);
	dm.featureManager.addEventListener("feature_removed", onFeatureRemoved);

	dm.dispatchEvent({type: "feature_created", feature, visual});

	return feature.id;
}

export function getNativeProfile(dm, featureId){
	const visual = dm.getVisual(featureId);
	return visual && visual.userData.nativeProfile ? visual.userData.nativeProfile.profile : null;
}
